import { formatDateTime } from '@/lib/dateFormat';

/**
 * Turns an audit row's OldValue / NewValue JSON into a readable field-level
 * trail — "Held by: Alex Smith → Jo Bloggs" rather than a blob of ids.
 *
 * This matters most for **held by**: `POST /assets/{id}/held-by` updates the
 * open assignment in place, so the assignment row only ever shows the current
 * holder. The audit row is the ONLY record that an earlier person held the
 * asset, which is exactly what a handover needs to show.
 *
 * The `*Name` keys this leans on come from two places: payloads written from
 * 20/08/2026 carry them, and `GET /api/audit` resolves the rest from the bare ids
 * on the way out (see AuditNameResolver). Either way the id is suppressed here
 * once a name is available — and when no name can be found, the raw id still
 * shows, which is honest rather than blank.
 */

export interface AuditChange {
  /** Human label, e.g. "Held by". */
  field: string;
  from: string;
  to: string;
}

/** Raw JSON key → the label shown in the trail. Unlisted keys are de-cased. */
const FIELD_LABELS: Record<string, string> = {
  kind: 'Assigned to',
  AssigneeKind: 'Assigned to',
  PersonName: 'Person',
  LocationName: 'Location',
  FleetItemId: 'Fleet item',
  HeldByPersonName: 'Held by',
  AssetLocationId: 'Device location',
  AssetLocationName: 'Device location',
  ParentLocationName: 'Part of site',
  ManagerPersonName: 'Manager',
  OwnerPersonName: 'Owner',
  RenewalOwnerPersonName: 'Renewal owner',
  ContractName: 'Contract',
  AssignedDate: 'Assigned',
  status: 'Status',
  LifecycleState: 'Status',
};

/**
 * Internal surrogate keys that mean nothing to a reader. They are not ids of a
 * thing anyone can look up — unlike PersonId, which the API resolves to a name —
 * so showing them is pure noise in a trail meant for humans.
 */
const NOISE_KEYS = new Set(['AssignmentId', 'AuditId']);

/** Action code → what actually happened, in words. */
const ACTION_LABELS: Record<string, string> = {
  Assign: 'Assigned',
  Return: 'Returned',
  SetHeldBy: 'Held by changed',
  SetStatus: 'Status changed',
  Create: 'Created',
  Update: 'Edited',
  Delete: 'Deleted',
  Restore: 'Restored',
  Link: 'Linked',
  Unlink: 'Unlinked',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

/**
 * Keys that only carry an id whose sibling `*Name` is already shown. Dropping
 * them is what keeps the trail readable — nobody reads "HeldByPersonId: 4 → 7".
 */
function isRedundantIdKey(key: string, keys: Set<string>): boolean {
  return key.endsWith('Id') && keys.has(`${key.slice(0, -2)}Name`);
}

function label(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // "HeldByPersonName" → "Held by person name" → sentence case.
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

function display(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && ISO_DATE.test(value)) return formatDateTime(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parse(json: string | null): Record<string, unknown> {
  if (!json) return {};
  try {
    const v: unknown = JSON.parse(json);
    return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * Field-level diff of one audit row. Keys present on both sides with the same
 * value are dropped, as are keys that are empty on both sides — a Create row
 * would otherwise list every null column it was born with.
 */
export function auditChanges(oldJson: string | null, newJson: string | null): AuditChange[] {
  const before = parse(oldJson);
  const after = parse(newJson);
  // New-side key order first (it reads as "what this action set"), then any key
  // that only existed before — e.g. what a Return closed.
  const keys = [...Object.keys(after), ...Object.keys(before).filter((k) => !(k in after))];
  const keySet = new Set(keys);

  const changes: AuditChange[] = [];
  for (const key of keys) {
    if (NOISE_KEYS.has(key)) continue;
    if (isRedundantIdKey(key, keySet)) continue;
    const from = display(before[key]);
    const to = display(after[key]);
    if (from === to) continue;
    if (from === '—' && to === '—') continue;
    changes.push({ field: label(key), from, to });
  }
  return changes;
}
